import React, { useState, useEffect } from 'react';
import { PRONOUN_OPTIONS, PRONOUN_OTHER, PRONOUN_MAX_LENGTH, pronounSelectValue } from '../lib/pronouns';

/**
 * PronounsPicker — a <select> of common pronouns plus a self-describe
 * ("Other…") escape hatch. The stored value is a plain string (the backend
 * field is Optional[str]); selecting "Prefer not to say" stores ''. Legacy or
 * off-list values display under "Other…" verbatim and are never coerced —
 * matching against the option list is case- and space-sensitive.
 *
 * Props:
 *  - value: string
 *  - onChange: (string) => void
 *  - id: string — associates the <select> with an external <label>
 *  - compact: boolean — smaller styling for the SignatureEditor field row
 *  - className: string — extra classes on the wrapper
 *  - ariaLabel: string — accessible label for the <select>
 */
export default function PronounsPicker({ value, onChange, id, compact, className = '', ariaLabel }) {
    const [otherSelected, setOtherSelected] = useState(() => pronounSelectValue(value) === PRONOUN_OTHER);

    // Reset the "Other" branch when the value is externally set to a known
    // option (e.g. on profile load). We deliberately do NOT reset on '' or on
    // custom values, so the "Other, nothing typed yet" transient is preserved.
    useEffect(() => {
        if (PRONOUN_OPTIONS.some(o => o.value === value)) setOtherSelected(false);
    }, [value]);

    const isOther = otherSelected || pronounSelectValue(value) === PRONOUN_OTHER;
    const selectValue = otherSelected ? PRONOUN_OTHER : pronounSelectValue(value);

    const handleSelect = (e) => {
        const next = e.target.value;
        if (next === PRONOUN_OTHER) {
            setOtherSelected(true);
            // Clear a known/blank value so the text input starts empty; keep a
            // custom value so a legacy string stays visible while editing.
            if (pronounSelectValue(value) !== PRONOUN_OTHER) onChange('');
        } else {
            setOtherSelected(false);
            onChange(next); // '' for "Prefer not to say", or the option value
        }
    };

    const handleOtherBlur = () => {
        onChange((value || '').trim());
    };

    const wrapperClass = compact
        ? `flex flex-col gap-1 flex-1 min-w-0 ${className}`
        : `w-full ${className}`;

    const selectClass = compact
        ? 'w-full px-2 py-1 text-xs border border-stone-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-stone-900/20'
        : 'w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20';

    const otherInputClass = compact
        ? 'w-full px-2 py-1 text-xs border border-stone-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-stone-900/20'
        : 'mt-2 w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20';

    return (
        <div className={wrapperClass}>
            <select
                id={id}
                value={selectValue}
                onChange={handleSelect}
                aria-label={ariaLabel || 'Pronouns'}
                data-testid="pronouns-select"
                className={selectClass}
            >
                <option value="">Prefer not to say</option>
                {PRONOUN_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
                <option value={PRONOUN_OTHER}>Other…</option>
            </select>
            {isOther && (
                <input
                    type="text"
                    value={value || ''}
                    onChange={e => onChange(e.target.value)}
                    onBlur={handleOtherBlur}
                    maxLength={PRONOUN_MAX_LENGTH}
                    aria-label="Custom pronouns"
                    data-testid="pronouns-other"
                    placeholder="Type your pronouns"
                    className={otherInputClass}
                />
            )}
        </div>
    );
}
